import React, { useState } from "react";
import { UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ButtonLoading } from "go-form";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import { FormDirty } from "go-form/components/FormDirty";
import { useCustomErrors } from "go-form/hooks";
import { apiOrganization } from "../../../../../../../../../services/Api/Organization/apiOrganization";
import { CompanyApi, JpkExportBodyApi } from "../../../../../../../../../services/Api/Organization/types";
import JpkExportBasicInfoForm from "./JpkExportBasicInfoForm";
import JpkExportDetailsForm from "./JpkExportDetailsForm";

interface Props {
	companyDetails: CompanyApi;
	form: UseFormReturn<JpkExportBodyApi>;
}

const JpkExportForm = ({ companyDetails, form }: Props) => {
	const [loading, setLoading] = useState(false);
	const { formState, handleSubmit, setError, getValues, reset, watch } = form;
	const { addFlash, addSuccessFlash } = useFlash();
	const { t } = useTranslation();
	const { setErrors, validateCustomErrors } = useCustomErrors(setError);

	const onSubmit = handleSubmit(async (data: JpkExportBodyApi) => {
		if (!validateCustomErrors()) {
			return;
		}

		setLoading(true);
		try {
			const res = await apiOrganization.exportJpkFile(data);
			downloadJpk(res);
			reset(watch());
			addSuccessFlash(t("lib:common.flash.completed"));
		} catch (err) {
			handleError.form(err, setError, addFlash);
		}
		setLoading(false);
	});

	const downloadJpk = (data: string) => {
		const url = window.URL.createObjectURL(new Blob([data]));
		const link = document.createElement("a");
		link.href = url;
		const date = getValues("date_range").split(" - ")[0].split(" ")[0];
		link.setAttribute("download", `${getValues("file_type")}_${date}.xml`);
		document.body.appendChild(link);
		link.click();
	};

	const buttonSubmitCustom = (
		<ButtonLoading loading={loading} variant="primary" type="submit">
			{t("lib:common.action.generate")}
		</ButtonLoading>
	);

	return (
		<FormDirty
			formState={formState}
			onSubmit={onSubmit}
			loading={loading}
			className="jpk-form"
			buttonSubmitCustom={buttonSubmitCustom}
		>
			<JpkExportBasicInfoForm form={form} setErrors={setErrors} />
			<JpkExportDetailsForm form={form} companyDetails={companyDetails} setErrors={setErrors} />
		</FormDirty>
	);
};

export default JpkExportForm;
